import { useState, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { motion } from "framer-motion";
import {
  ArrowLeft, Signal, Smartphone, CreditCard, Trash2, Edit, PowerOff, RefreshCw,
  Users, Tag, UsersRound, Sliders, AlertTriangle, Loader2, QrCode,
} from "lucide-react";
import { toast } from "sonner";
import { AdminNumberEditDialog } from "@/components/manager/AdminNumberEditDialog";

export default function ManagerAccountDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin_account_full", id],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "account_full_detail", account_id: id },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const [editInst, setEditInst] = useState<any>(null);
  const [delAccountOpen, setDelAccountOpen] = useState(false);
  const [delAccountInput, setDelAccountInput] = useState("");
  const [delConfirm, setDelConfirm] = useState<{ kind: string; id: string; label: string; table?: string } | null>(null);
  const [qrInst, setQrInst] = useState<any>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [selContacts, setSelContacts] = useState<Set<string>>(new Set());
  const [selTags, setSelTags] = useState<Set<string>>(new Set());
  const [selGroups, setSelGroups] = useState<Set<string>>(new Set());
  const [selProfiles, setSelProfiles] = useState<Set<string>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState<{ table: string; ids: string[]; label: string; clear: () => void } | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const toggleSet = (s: Set<string>, id: string) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  };

  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  if (isLoading) {
    return (
      <div className="space-y-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/manager/accounts")}><ArrowLeft className="h-5 w-5" /></Button>
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const profile = data?.profile;
  const sub = data?.subscription;
  const instances: any[] = data?.instances || [];
  const customContacts: any[] = data?.customContacts || [];
  const contactTags: any[] = data?.contactTags || [];
  const customGroups: any[] = data?.customGroups || [];
  const customProfiles: any[] = data?.customProfiles || [];
  const systemProfiles: any[] = data?.systemProfiles || [];
  const tagJoins: any[] = data?.contactTagJoins || [];
  const allProfiles = [...systemProfiles, ...customProfiles];

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["admin_account_full", id] });
    await refetch();
  };

  const deleteAccount = async () => {
    try {
      const { error } = await supabase.functions.invoke("delete-account", {
        body: { target_user_id: id },
      });
      if (error) throw error;
      toast.success("Conta excluída");
      navigate("/manager/accounts");
    } catch (e: any) {
      toast.error(e.message || "Erro ao excluir conta");
    }
  };

  const deleteInstance = async (instId: string) => {
    try {
      const { error } = await supabase.functions.invoke("disconnect-instance", {
        body: { instance_id: instId, action: "delete" },
      });
      if (error) throw error;
      toast.success("Número excluído");
      refresh();
    } catch (e: any) {
      toast.error(e.message || "Erro ao excluir");
    }
  };

  const disconnectInstance = async (instId: string) => {
    try {
      const { error } = await supabase.functions.invoke("disconnect-instance", {
        body: { instance_id: instId },
      });
      if (error) throw error;
      toast.success("Desconectado");
      refresh();
    } catch (e: any) {
      toast.error(e.message || "Erro");
    }
  };

  const reconnectInstance = async (inst: any) => {
    setQrInst(inst);
    setQrCode(null);
    setQrLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("disconnect-instance", {
        body: { instance_id: inst.id, action: "restart" },
      });
      if (error) throw error;
      if (data?.qr_code) setQrCode(data.qr_code);
      // Poll
      pollingRef.current = setInterval(async () => {
        const { data: p } = await supabase.functions.invoke("get-qrcode", { body: { instance_id: inst.id } });
        if (p?.state === "open") {
          if (pollingRef.current) clearInterval(pollingRef.current);
          setQrInst(null);
          refresh();
        } else if (p?.qr_code) setQrCode(p.qr_code);
      }, 5000);
    } catch (e: any) {
      toast.error(e.message || "Erro ao reconectar");
    } finally {
      setQrLoading(false);
    }
  };

  const closeQr = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setQrInst(null);
  };

  const deleteRow = async (table: string, rowId: string) => {
    try {
      const { error } = await supabase.functions.invoke("admin-stats", {
        body: { section: "delete_row", table, row_id: rowId },
      });
      if (error) throw error;
      toast.success("Removido");
      refresh();
    } catch (e: any) {
      toast.error(e.message || "Erro");
    }
  };

  const bulkDelete = async (table: string, ids: string[], clear: () => void) => {
    if (!ids.length) return;
    let ok = 0; let fail = 0;
    for (const rid of ids) {
      try {
        const { error } = await supabase.functions.invoke("admin-stats", {
          body: { section: "delete_row", table, row_id: rid },
        });
        if (error) throw error;
        ok++;
      } catch { fail++; }
    }
    clear();
    if (ok) toast.success(`${ok} removido(s)${fail ? ` • ${fail} falha(s)` : ""}`);
    else toast.error("Falha ao remover");
    refresh();
  };

  const tagsByContact = new Map<string, string[]>();
  for (const j of tagJoins) {
    const arr = tagsByContact.get(j.contact_id) || [];
    arr.push(j.tag_id);
    tagsByContact.set(j.contact_id, arr);
  }
  const tagById = new Map(contactTags.map((t: any) => [t.id, t]));

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/manager/accounts")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold text-foreground truncate">{data?.email || "Conta"}</h1>
            <p className="text-xs text-muted-foreground font-mono truncate">{id}</p>
          </div>
        </div>
        <Button variant="destructive" size="sm" onClick={() => setDelAccountOpen(true)}>
          <Trash2 className="h-4 w-4 mr-1.5" /> Excluir conta
        </Button>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center">
                <Smartphone className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Portas</p>
                <p className="font-display text-xl font-bold">{instances.length}/{profile?.max_ports || 0}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}>
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center">
                <Signal className="h-5 w-5 text-accent" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Online</p>
                <p className="font-display text-xl font-bold">
                  {instances.filter((i) => i.evolution_connection_state === "open").length}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="w-11 h-11 rounded-xl bg-secondary/60 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Assinatura</p>
                <p className="font-display text-lg font-bold">
                  {sub ? (
                    <Badge className={sub.status === "active" ? "bg-accent/20 text-accent border-0" : "bg-yellow-500/20 text-yellow-400 border-0"}>
                      {sub.status === "active" ? "Ativo" : sub.status === "trialing" ? "Trial" : sub.status}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">Free</Badge>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <Tabs defaultValue="numbers">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="numbers"><Smartphone className="h-3.5 w-3.5 mr-1.5" />Números ({instances.length})</TabsTrigger>
          <TabsTrigger value="contacts"><Users className="h-3.5 w-3.5 mr-1.5" />Contatos ({customContacts.length})</TabsTrigger>
          <TabsTrigger value="tags"><Tag className="h-3.5 w-3.5 mr-1.5" />Tags ({contactTags.length})</TabsTrigger>
          <TabsTrigger value="groups"><UsersRound className="h-3.5 w-3.5 mr-1.5" />Grupos ({customGroups.length})</TabsTrigger>
          <TabsTrigger value="profiles"><Sliders className="h-3.5 w-3.5 mr-1.5" />Perfis ({customProfiles.length})</TabsTrigger>
          <TabsTrigger value="subscription"><CreditCard className="h-3.5 w-3.5 mr-1.5" />Assinatura</TabsTrigger>
        </TabsList>

        {/* NUMBERS */}
        <TabsContent value="numbers" className="mt-4">
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-warmz-glass-border/30">
                    <TableHead>Número</TableHead>
                    <TableHead>Apelido</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Perfil</TableHead>
                    <TableHead>Health</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!instances.length ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Nenhum número conectado</TableCell></TableRow>
                  ) : instances.map((inst) => {
                    const isOnline = inst.evolution_connection_state === "open";
                    return (
                      <TableRow key={inst.id}>
                        <TableCell><span className="font-mono text-sm">{inst.phone_number}</span></TableCell>
                        <TableCell><span className="text-sm">{inst.nickname || "—"}</span></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 rounded-full ${isOnline ? "bg-accent animate-pulse" : "bg-destructive"}`} />
                            <span className={`text-xs font-medium ${isOnline ? "text-accent" : "text-destructive"}`}>{isOnline ? "Online" : "Offline"}</span>
                          </div>
                        </TableCell>
                        <TableCell><span className="text-sm">{inst.warmup_profiles?.ui_icon || "⚙️"} {inst.warmup_profiles?.name || "—"}</span></TableCell>
                        <TableCell><span className="text-sm font-bold">{inst.health_score || 0}</span></TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" onClick={() => setEditInst(inst)} title="Editar">
                              <Edit className="h-4 w-4" />
                            </Button>
                            {isOnline ? (
                              <Button size="icon" variant="ghost" onClick={() => disconnectInstance(inst.id)} title="Desconectar">
                                <PowerOff className="h-4 w-4 text-yellow-400" />
                              </Button>
                            ) : (
                              <Button size="icon" variant="ghost" onClick={() => reconnectInstance(inst)} title="Reconectar (QR)">
                                <RefreshCw className="h-4 w-4 text-primary" />
                              </Button>
                            )}
                            <Button size="icon" variant="ghost" onClick={() => setDelConfirm({ kind: "instance", id: inst.id, label: inst.phone_number })} title="Excluir">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONTACTS */}
        <TabsContent value="contacts" className="mt-4 space-y-3">
          {selContacts.size > 0 && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <span className="text-sm">{selContacts.size} contato(s) selecionado(s)</span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelContacts(new Set())}>Limpar</Button>
                <Button size="sm" variant="destructive" onClick={() => setBulkConfirm({ table: "custom_contacts", ids: Array.from(selContacts), label: `${selContacts.size} contato(s)`, clear: () => setSelContacts(new Set()) })}>
                  <Trash2 className="h-4 w-4 mr-1.5" /> Excluir selecionados
                </Button>
              </div>
            </div>
          )}
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-warmz-glass-border/30">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={customContacts.length > 0 && selContacts.size === customContacts.length}
                        onCheckedChange={(v) => setSelContacts(v ? new Set(customContacts.map((c: any) => c.id)) : new Set())}
                      />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!customContacts.length ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Sem contatos</TableCell></TableRow>
                  ) : customContacts.map((c) => {
                    const tagIds = tagsByContact.get(c.id) || [];
                    return (
                      <TableRow key={c.id} data-state={selContacts.has(c.id) ? "selected" : undefined}>
                        <TableCell><Checkbox checked={selContacts.has(c.id)} onCheckedChange={() => setSelContacts((s) => toggleSet(s, c.id))} /></TableCell>
                        <TableCell className="text-sm">{c.name}</TableCell>
                        <TableCell><span className="font-mono text-xs">{c.phone_number}</span></TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {tagIds.map((tid) => {
                              const t: any = tagById.get(tid);
                              if (!t) return null;
                              return <Badge key={tid} style={{ backgroundColor: `${t.color}20`, color: t.color, border: 0 }} className="text-[10px]">{t.name}</Badge>;
                            })}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={c.is_active ? "text-accent border-accent/30" : "text-muted-foreground"}>
                            {c.is_active ? "Ativo" : "Inativo"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" onClick={() => setDelConfirm({ kind: "row", id: c.id, label: c.name, table: "custom_contacts" })}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAGS */}
        <TabsContent value="tags" className="mt-4 space-y-3">
          {selTags.size > 0 && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <span className="text-sm">{selTags.size} tag(s) selecionada(s)</span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelTags(new Set())}>Limpar</Button>
                <Button size="sm" variant="destructive" onClick={() => setBulkConfirm({ table: "contact_tags", ids: Array.from(selTags), label: `${selTags.size} tag(s)`, clear: () => setSelTags(new Set()) })}>
                  <Trash2 className="h-4 w-4 mr-1.5" /> Excluir selecionadas
                </Button>
              </div>
            </div>
          )}
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-4">
              {!contactTags.length ? (
                <p className="text-sm text-muted-foreground text-center py-8">Sem tags</p>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-warmz-glass-border/30">
                    <Checkbox
                      checked={selTags.size === contactTags.length}
                      onCheckedChange={(v) => setSelTags(v ? new Set(contactTags.map((t: any) => t.id)) : new Set())}
                    />
                    <span className="text-xs text-muted-foreground">Selecionar todas</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {contactTags.map((t: any) => (
                      <div key={t.id} className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                        <div className="flex items-center gap-2 min-w-0">
                          <Checkbox checked={selTags.has(t.id)} onCheckedChange={() => setSelTags((s) => toggleSet(s, t.id))} />
                          <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                          <span className="text-sm truncate">{t.name}</span>
                        </div>
                        <Button size="icon" variant="ghost" onClick={() => setDelConfirm({ kind: "row", id: t.id, label: t.name, table: "contact_tags" })}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GROUPS */}
        <TabsContent value="groups" className="mt-4 space-y-3">
          {selGroups.size > 0 && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <span className="text-sm">{selGroups.size} grupo(s) selecionado(s)</span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelGroups(new Set())}>Limpar</Button>
                <Button size="sm" variant="destructive" onClick={() => setBulkConfirm({ table: "warmup_groups", ids: Array.from(selGroups), label: `${selGroups.size} grupo(s)`, clear: () => setSelGroups(new Set()) })}>
                  <Trash2 className="h-4 w-4 mr-1.5" /> Excluir selecionados
                </Button>
              </div>
            </div>
          )}
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-warmz-glass-border/30">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={customGroups.length > 0 && selGroups.size === customGroups.length}
                        onCheckedChange={(v) => setSelGroups(v ? new Set(customGroups.map((g: any) => g.id)) : new Set())}
                      />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Convite</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!customGroups.length ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Sem grupos customizados</TableCell></TableRow>
                  ) : customGroups.map((g: any) => (
                    <TableRow key={g.id} data-state={selGroups.has(g.id) ? "selected" : undefined}>
                      <TableCell><Checkbox checked={selGroups.has(g.id)} onCheckedChange={() => setSelGroups((s) => toggleSet(s, g.id))} /></TableCell>
                      <TableCell className="text-sm">{g.name}</TableCell>
                      <TableCell><Badge variant="outline">{g.category}</Badge></TableCell>
                      <TableCell><span className="font-mono text-xs text-muted-foreground">{g.invite_code}</span></TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" onClick={() => setDelConfirm({ kind: "row", id: g.id, label: g.name, table: "warmup_groups" })}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PROFILES */}
        <TabsContent value="profiles" className="mt-4 space-y-3">
          {selProfiles.size > 0 && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/30">
              <span className="text-sm">{selProfiles.size} perfil(s) selecionado(s)</span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelProfiles(new Set())}>Limpar</Button>
                <Button size="sm" variant="destructive" onClick={() => setBulkConfirm({ table: "warmup_profiles", ids: Array.from(selProfiles), label: `${selProfiles.size} perfil(s)`, clear: () => setSelProfiles(new Set()) })}>
                  <Trash2 className="h-4 w-4 mr-1.5" /> Excluir selecionados
                </Button>
              </div>
            </div>
          )}
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-warmz-glass-border/30">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={customProfiles.length > 0 && selProfiles.size === customProfiles.length}
                        onCheckedChange={(v) => setSelProfiles(v ? new Set(customProfiles.map((p: any) => p.id)) : new Set())}
                      />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Msgs/dia</TableHead>
                    <TableHead>Janela</TableHead>
                    <TableHead>Stories</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!customProfiles.length ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Sem perfis customizados</TableCell></TableRow>
                  ) : customProfiles.map((p: any) => (
                    <TableRow key={p.id} data-state={selProfiles.has(p.id) ? "selected" : undefined}>
                      <TableCell><Checkbox checked={selProfiles.has(p.id)} onCheckedChange={() => setSelProfiles((s) => toggleSet(s, p.id))} /></TableCell>
                      <TableCell className="text-sm">{p.ui_icon || "⚙️"} {p.name}</TableCell>
                      <TableCell className="text-sm">{p.min_daily_msgs}–{p.max_daily_msgs}</TableCell>
                      <TableCell className="text-sm">{(p.period_start || "").slice(0,5)}–{(p.period_end || "").slice(0,5)}</TableCell>
                      <TableCell><span className="text-sm">{p.status_enabled ? `${p.status_per_day}/d` : "—"}</span></TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" onClick={() => setDelConfirm({ kind: "row", id: p.id, label: p.name, table: "warmup_profiles" })}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SUBSCRIPTION */}
        <TabsContent value="subscription" className="mt-4">
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-5 space-y-2 text-sm">
              {sub ? (
                <>
                  <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span>{sub.status}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Quantidade</span><span>{sub.quantity}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Início período</span><span>{new Date(sub.current_period_start).toLocaleString("pt-BR")}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Fim período</span><span>{new Date(sub.current_period_end).toLocaleString("pt-BR")}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Cancelamento agendado</span><span>{sub.cancel_at_period_end ? "Sim" : "Não"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Stripe Subscription ID</span><span className="font-mono text-xs">{sub.id}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Price ID</span><span className="font-mono text-xs">{sub.price_id}</span></div>
                </>
              ) : (
                <p className="text-muted-foreground">Sem assinatura ativa</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit instance dialog */}
      <AdminNumberEditDialog
        open={!!editInst}
        onOpenChange={(v) => !v && setEditInst(null)}
        instance={editInst}
        profiles={allProfiles}
        onSaved={refresh}
      />

      {/* QR Reconnect dialog */}
      <Dialog open={!!qrInst} onOpenChange={(v) => !v && closeQr()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reconectar {qrInst?.phone_number}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center py-4">
            {qrLoading || !qrCode ? (
              <div className="w-48 h-48 rounded-2xl bg-secondary/40 flex items-center justify-center">
                {qrLoading ? <Loader2 className="h-8 w-8 animate-spin" /> : <QrCode className="h-16 w-16 text-muted-foreground/50" />}
              </div>
            ) : (
              <img src={qrCode.startsWith("data:") ? qrCode : `data:image/png;base64,${qrCode}`} alt="QR" className="w-48 h-48 rounded-2xl" />
            )}
          </div>
          <p className="text-xs text-muted-foreground text-center">Escaneie no WhatsApp do usuário</p>
        </DialogContent>
      </Dialog>

      {/* Delete account confirm */}
      <AlertDialog open={delAccountOpen} onOpenChange={setDelAccountOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-destructive" /> Excluir conta</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove TODOS os dados desta conta (números, contatos, grupos, assinatura). Digite <strong>EXCLUIR</strong> para confirmar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={delAccountInput} onChange={(e) => setDelAccountInput(e.target.value)} placeholder="EXCLUIR" />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDelAccountInput("")}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={delAccountInput !== "EXCLUIR"}
              onClick={() => { setDelAccountInput(""); deleteAccount(); }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir conta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={!!bulkConfirm} onOpenChange={(v) => !v && setBulkConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-destructive" /> Exclusão em massa</AlertDialogTitle>
            <AlertDialogDescription>
              Excluir <strong>{bulkConfirm?.label}</strong>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (bulkConfirm) bulkDelete(bulkConfirm.table, bulkConfirm.ids, bulkConfirm.clear);
                setBulkConfirm(null);
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir tudo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Generic delete confirm */}
      <AlertDialog open={!!delConfirm} onOpenChange={(v) => !v && setDelConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Excluir <strong>{delConfirm?.label}</strong>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!delConfirm) return;
                if (delConfirm.kind === "instance") deleteInstance(delConfirm.id);
                else if (delConfirm.kind === "row" && delConfirm.table) deleteRow(delConfirm.table, delConfirm.id);
                setDelConfirm(null);
              }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
